export const COPILOT_TARGET_TYPES = [
  'WEB_APPLICATION',
  'AI_APPLICATION',
  'EXTERNAL_NETWORK',
  'INTERNAL_NETWORK',
] as const;

export type CopilotTargetType = (typeof COPILOT_TARGET_TYPES)[number];

export const COPILOT_SECURITY_DOMAINS = [
  'web-app',
  'external-network',
  'internal-network',
] as const;

export type CopilotSecurityDomain = (typeof COPILOT_SECURITY_DOMAINS)[number];

export type CopilotViewContext = {
  pathname: string;
  entityType?: string;
  entityId?: string;
};

export const HOST_INVENTORY_PROBED_TOOLS = [
  'nmap',
  'masscan',
  'nuclei',
  'ffuf',
  'sqlmap',
  'httpx',
  'nikto',
  'hydra',
  'curl',
  'wget',
  'python3',
  'node',
  'ssh',
  'docker',
  'kubectl',
  'msfconsole',
  'impacket-smbclient',
] as const;

export type HostInventory = {
  platform: string;
  arch: string;
  osRelease: string;
  defaultShell: string | null;
  availableShells: string[];
  availableTools: string[];
  capturedAt: number;
};

export type CopilotInvokeTargetHint = {
  targetId?: string;
  targetType?: CopilotTargetType;
  securityDomain?: CopilotSecurityDomain;
  view?: CopilotViewContext;
};

export type CopilotFindingTargetRecord = {
  applicationId: string;
  targetId: string;
  targetType: string;
};

export const COPILOT_ANSWER_SCOPES = [
  'target',
  'attackSurface',
  'tenant',
] as const;

export type CopilotAnswerScope = (typeof COPILOT_ANSWER_SCOPES)[number];

export type CopilotWorkingAnswerScope =
  | { scope: 'tenant' }
  | { scope: 'attackSurface'; targetType: CopilotTargetType };

export function isCopilotAnswerScope(
  value: string | null | undefined,
): value is CopilotAnswerScope {
  return (
    typeof value === 'string' &&
    (COPILOT_ANSWER_SCOPES as readonly string[]).includes(value)
  );
}

export function isCopilotTargetType(
  value: string | null | undefined,
): value is CopilotTargetType {
  return (
    typeof value === 'string' &&
    (COPILOT_TARGET_TYPES as readonly string[]).includes(value)
  );
}

export function isApplicationClassTarget(
  targetType: CopilotTargetType | undefined,
): boolean {
  return targetType === 'WEB_APPLICATION' || targetType === 'AI_APPLICATION';
}

export function resolveCopilotBoundTargetId({
  targetType,
  applicationId,
  surfaceId,
}: {
  targetType?: CopilotTargetType;
  applicationId?: string;
  surfaceId?: string;
}): string | undefined {
  const normalizedApplicationId = applicationId?.trim() || undefined;
  const normalizedSurfaceId = surfaceId?.trim() || undefined;

  if (targetType && !isApplicationClassTarget(targetType)) {
    return normalizedSurfaceId ?? normalizedApplicationId;
  }

  return normalizedApplicationId ?? normalizedSurfaceId;
}

export function securityDomainForTargetType(
  targetType: CopilotTargetType,
): CopilotSecurityDomain {
  switch (targetType) {
    case 'EXTERNAL_NETWORK':
      return 'external-network';
    case 'INTERNAL_NETWORK':
      return 'internal-network';
    case 'WEB_APPLICATION':
    case 'AI_APPLICATION':
      return 'web-app';
  }
}

export function resolveCopilotInvokeTargetFromFinding(
  finding: CopilotFindingTargetRecord,
): Pick<
  CopilotInvokeTargetHint,
  'targetId' | 'targetType' | 'securityDomain'
> | null {
  if (!finding.applicationId && !finding.targetId) {
    return null;
  }

  if (finding.targetType === 'EXTERNAL_NETWORK') {
    return {
      targetId: finding.targetId,
      targetType: 'EXTERNAL_NETWORK',
      securityDomain: securityDomainForTargetType('EXTERNAL_NETWORK'),
    };
  }

  if (finding.targetType === 'INTERNAL_NETWORK') {
    return {
      targetId: finding.targetId,
      targetType: 'INTERNAL_NETWORK',
      securityDomain: securityDomainForTargetType('INTERNAL_NETWORK'),
    };
  }

  if (finding.targetType === 'AI_APPLICATION') {
    return {
      targetId: finding.applicationId,
      targetType: 'AI_APPLICATION',
      securityDomain: securityDomainForTargetType('AI_APPLICATION'),
    };
  }

  return {
    targetId: finding.applicationId,
    targetType: 'WEB_APPLICATION',
    securityDomain: securityDomainForTargetType('WEB_APPLICATION'),
  };
}
